// Media upload & AI message tools helpers (WP-04).
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from './firebase';
import { stripUndefined } from './callable';

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

export const uploadMediaToMeta = call<
  { storagePath: string; mimeType?: string; asVoiceNote?: boolean },
  { ok: boolean; mediaId: string; mimeType: string }
>('mediaUpload');

export const translateMessage = call<
  { conversationId?: string; messageId?: string; text?: string; targetLanguage: string },
  { ok: boolean; translated: string }
>('messageTranslate');

export const rewriteMessage = call<
  { text: string; tone: 'professional' | 'friendly' | 'concise' | 'persuasive' | 'casual' },
  { ok: boolean; rewritten: string }
>('messageRewrite');

/**
 * Uploads a client File/Blob to Firebase Storage under tenants/{tenantId}/inboxMedia/{fileId}
 */
export async function uploadInboxMediaFile(
  tenantId: string,
  file: File | Blob,
  fileName?: string,
): Promise<{ storagePath: string; downloadUrl: string; mimeType: string }> {
  const ext = fileName?.split('.').pop() || (file.type ? file.type.split('/')[1] : 'bin');
  const fileId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${ext}`;
  const storagePath = `tenants/${tenantId}/inboxMedia/${fileId}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || 'application/octet-stream',
  });
  const downloadUrl = await getDownloadURL(snapshot.ref);

  return { storagePath, downloadUrl, mimeType: file.type || 'application/octet-stream' };
}

/**
 * Uploads a canned image file to tenants/{tenantId}/canned/{fileId}
 */
export async function uploadCannedImageFile(
  tenantId: string,
  file: File | Blob,
  fileName?: string,
): Promise<{ storagePath: string; downloadUrl: string }> {
  const ext = fileName?.split('.').pop() || 'png';
  const fileId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${ext}`;
  const storagePath = `tenants/${tenantId}/canned/${fileId}`;
  const storageRef = ref(storage, storagePath);

  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type || 'image/png',
  });
  const downloadUrl = await getDownloadURL(snapshot.ref);

  return { storagePath, downloadUrl };
}
